import { observable, computed, action, makeObservable, flow } from "mobx";
import * as _ from "lodash";

import { EAIClient } from "Services/EAIClient";
import { appStore } from "../";

import { Dashboard } from "../dashboardStore";
import {User} from "Stores/userStore";

export type MapType = Student | Course | Committee

export type Mapping = "students" | "courses" | "committees"

export interface IDataQuery {
        total_count: number,
        data: Course[]
        total_pages: number,
        current_page: number
}

export interface CoursesDataQuery {
    data: Course[]
}

export interface MapTypeDataQuery {
    data: MapType[]
    result: DefaultResult
}
export interface Course {
    [idx: string]: string | string[] | number | undefined
    name: string
    instructors?: string[]
    term: string
    id: number
    catalog: string
}

export interface Faculty {
    email: string
    id: string
    name: string
}

export interface Committee {
    [idx: string]: string | number | Dashboard[] | Faculty[] | undefined
    id: number
    created: string
    updated: string
    name: string
    // extended information
    dashboards?: Dashboard[]
    faculty?: Faculty[]
}

interface DefaultResult {
    totalCount: number
}

export interface StudentsDataQuery {
    data: Student[]
    result: DefaultResult
}

export interface Student {
    [idx: string]: string | number | undefined | boolean
    cohort: string
    graduating_class: string
    ruuid: string
    mentor_email?: string
    mentor_name?: string
    status: string
    student_email: string
    schools_student_id?: string
    student_name: string
    first_name: string
    last_name: string
    term_description: string
    number_of_groups: number
    disabled?: boolean
}

export interface TableParams {
    dashboardId?: number
    pageSize?: number,
    page?: number,
    sortBy?: string,
    sortOrder?: string,
    filters?: Record<string, any>
    include_disabled?: boolean
}

export interface Sort {
    field: string
    isDescending: boolean
}

export interface Settings {
    attachments: {
        max_storage_gb: number
        max_file_size_mb: number
    }
}

export interface LicenseInfo {
    current_users: number
    max_number_users: number
}

export interface DashboardView {
    ruuid: string
    view_type: string
    comment_id: string
    url: string
}

export class CommonStore {

    [idx: string]: any

    @observable courses: Course[];
    @observable committees: Committee[];
    @observable students: Student[];
    @observable dashboardViews: DashboardView[] = [];
    @observable dashboardView: DashboardView | null = null;
    @observable totalStudents: number;
    @observable totalCourses: number;
    @observable licenseInfo: LicenseInfo;
    @observable isSchoolProcessing: boolean;
    @observable isStudentsFetching = false;
    @observable isFetchingDashboardViews = false;
    @observable isFetchingDashboardViewById = false;

    // TODO: This is a short-term implementation specifically for super admin.
    // We should eventually expand the scope to encompass the entire app
    @observable statusAlerts = new Map();

    @observable filters: {
        [idx: string]: Partial<Course> | Partial<Student> | Partial<Committee>
        courses: Partial<Course>
        students: Partial<Student>
        committees: Partial<Committee>
    } = {
        courses: {},
        students: {},
        committees: {}
    };

    @observable sorts: {
        [idx: string]: Sort | undefined
        courses?: Sort
        students?: Sort
        committees?: Sort
    } = {
        courses: {field: "name", isDescending: false},
        students: {field: "student_name", isDescending: false},
        committees: {field: "name", isDescending: false}
    };

    @observable settings: Settings = {
        attachments: {
            max_storage_gb: -1,
            max_file_size_mb: -1,
        }
    };

    constructor() {
        makeObservable(this);
    }

    @computed get filteredSortedCourses() {
        return this.filteredSortedListByType("courses");
    }
    @computed get filteredSortedFaculty() {
        return this.filteredSortedListByType("faculty");
    }
    @computed get filteredSortedStudents() {
        return this.filteredSortedListByType("students");
    }
    @computed get filteredSortedCommittees() {
        return this.filteredSortedListByType("committees");
    }
    @computed get activeUserFaculty() {
        return _.filter(this.faculty, {status: "active"});
    }

    client = new EAIClient();

    lastStudentFetch: number;
    lastCourseFetch: number;

    @flow
    *fetchCourses(fields?: string, params?: TableParams): any {
        try {
            const resp = yield this.client.getCourses(fields || undefined, params);
            this.totalCourses = resp.data.result.total_count;
            this.courses = resp.data.data;
        } catch(e) {
            appStore.addError(e);
        }
    }

    async fetchCoursesAsync(fields?: string, params?: TableParams) {
        this.lastCourseFetch = Date.now();
        try {
            const resp = await this.client.getCourses(fields || undefined, params, this.lastCourseFetch);
            if (resp.data.result.timestamp < this.lastCourseFetch) return;
            this.totalCourses = resp.data.result.total_count;
            this.courses = resp.data.data;
        } catch(e) {
            appStore.addError(e);
        }
    }

    @flow
    *fetchStudents (params?: TableParams): any {
        try {
            this.isStudentsFetching = true;
            const resp = yield this.client.getStudents(params);
            this.totalStudents = resp.data.result.total_count;
            this.students = resp.data.data;
            return this.students;
        } catch(e) {
            appStore.addError(e);
        } finally {
            this.isStudentsFetching = false;
        }
    }

    fetchDashboardViews = flow(function *(this: CommonStore, dashboardId: number | undefined | null, actionplanRuuid: string | undefined) {
        if (!dashboardId || !actionplanRuuid) {
            return;
        }
        try {
            this.isFetchingDashboardViews = true;
            const resp = yield this.client.getDashboardViews(dashboardId, actionplanRuuid);
            this.dashboardViews = resp.data.data;
            return this.dashboardViews;
        } catch (e) {
            appStore.addError(e);
        } finally {
            this.isFetchingDashboardViews = false;
        }
    });

    fetchDashboardViewById = flow(function *(this: CommonStore, viewId: string) {
        try {
            this.isFetchingDashboardViewById = true;
            const resp = yield this.client.getDashboardViewById(viewId);
            this.dashboardView = resp.data.data;
            return this.dashboardView;
        } catch (e) {
            appStore.addError(e);
        } finally {
            this.isFetchingDashboardViewById = false;
        }
    });

    fetchUsersFromStudentGroup = flow(function *(this: CommonStore, studentGroupRuuid: string) {
      try {
        const resp = yield this.client.fetchUsersFromStudentGroup(studentGroupRuuid);
            return resp.data.data as User[];
      } catch (e) {
          appStore.addError(e);
      }
    });

    async fetchStudentsAsync (params?: TableParams) {
        this.lastStudentFetch = Date.now();
        try {
            const resp = await this.client.getStudents(params, this.lastStudentFetch);
            if (resp.data.result.timestamp < this.lastStudentFetch) return;
            this.totalStudents = resp.data.result.total_count;
            this.students = resp.data.data;
        } catch(e) {
            appStore.addError(e);
        }
    }

    @flow
    *fetchCommittees(): any {
        // TODO: use requestStore to avoid several requests
        try {
            const resp = yield this.client.getCommittees();
            this.committees = resp.data.data;
        } catch(e) {
            appStore.addError(e);
        }
    }

    @flow
    *fetchLicenseInfo () {
        try {
            const resp = yield this.client.fetchLicenseInfo();
            this.licenseInfo = resp.data.data;
        } catch(e) {
            appStore.addError(e);
        }
    }

    @action
    resetFilters(mapping: Mapping) {
        this.filters[mapping] = {};
    }

    filterList(dataSet: "faculty" | "courses" | "students" | "committees") {
        if(!this[dataSet]) return undefined;
        const data: Course[] | Student[] = this[dataSet].slice();
        const predicate = this.filters[dataSet];
        if( !predicate || Object.keys(predicate).length === 0 ) return data;

        // since there can be more than one thing to filter by,
        // we have to iterate over every key in predicate object
        const filtered = _.filter(data, (d: Course | Student) => {
            let isIncluded = false;
            const predicateObj = this.filters[dataSet];
            // When filtering by multiple predicates matches need to
            // be returned immediately, otherwise only rows that match
            // all compared columns will return true
            const predicateArray = Object.keys(predicateObj);
            const length = predicateArray.length;
            // forEach does not allow break - replacing with standard for loop
            for(let i = 0; i < length; i++){
                const k = predicateArray[i];
                const predicate = predicateObj[k];
                if( predicate ) {
                    const searchStr = predicate.toString().toLowerCase();
                    if(k === "id") {
                        isIncluded = isIncluded && d.id === predicateObj.id;
                    } else {
                        const thing = d[k];
                        if(thing) {
                            if(thing.toString().toLowerCase().indexOf(searchStr) > -1){
                                isIncluded = true;
                                break;
                            }
                        }
                    }
                }
            }
            return isIncluded;
        });
        return predicate.isDescending ? filtered : filtered.reverse();
    }

    filteredSortedListByType(type: "faculty" | "students" | "courses" | "committees") {
        let filteredSortedList: MapType[]; // Faculty[] | Student[] | Course[]
        if( !this.filters || !this.filters[type] ) {
            filteredSortedList = this[type];
        } else {
            filteredSortedList = this.filterList(type) as MapType[];
        }

        const sorts: Sort | undefined = this.sorts[type];
        if(sorts) {
            filteredSortedList = _.sortBy(filteredSortedList, sorts.field);
            if(sorts.isDescending) filteredSortedList.reverse();
        }
        return filteredSortedList;
    }

    @action
    addAlert(alert: string) {
        const id = Math.random();
        this.statusAlerts.set(id, alert);
        setTimeout(() => this.removeAlert(id), 15000);
    }

    @action
    removeAlert(id: number) {
        this.statusAlerts.delete(id);
    }

    @flow
    * getSettings(): any{
        try{
            const resp = yield this.client.getSettings();
            this.settings = resp.data.data;
        } catch(e){
            appStore.addError(e, "Unable to fetch settings. Please try again.");
        }
    }

    @flow
    * updateSettings(settingName: string, data: any): any{
        try{
            yield this.client.updateSettings(settingName, data);
        } catch(e) {
            appStore.addError(e, `Error updating ${settingName} settings. Please try again.`);
        } finally {
            this.getSettings();
        }
    }
}
